import React, { useRef } from 'react';
import {
    Controller,
    ControllerRenderProps,
    FieldValues,
    Path,
    RegisterOptions,
    useFormContext,
} from 'react-hook-form';

import { Textarea } from '@/components';

type TextareaWithSignatureProps<TFieldValues extends FieldValues> = {
    signature?: string | null;
    name: Path<TFieldValues>;
    placeholder?: string;
    required?: string;
    id?: string;
    rules?: RegisterOptions<TFieldValues, Path<TFieldValues>>;
};

const TextareaWithSignatureInner = <TFieldValues extends FieldValues>({
    signature,
    name,
    placeholder,
    required,
    id,
    rules,
}: TextareaWithSignatureProps<TFieldValues>) => {
    const { control, getValues, register } = useFormContext<TFieldValues>();

    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    // Magic, needed to work when switching internal or not
    const handleBlur = () => {};
    const handleFocus = () => {};

    const handleChange =
        (field: ControllerRenderProps<TFieldValues, Path<TFieldValues>>) =>
        (e: React.ChangeEvent<HTMLTextAreaElement>) => {
            const baseValue = e.target.value;
            const currentValue = getValues(name);
            if (currentValue == '' && signature) {
                const newValue = baseValue + '\n\n' + signature;
                if (textareaRef.current) {
                    const cursorPos = baseValue.length;
                    setTimeout(() => {
                        textareaRef.current!.selectionStart = cursorPos;
                        textareaRef.current!.selectionEnd = cursorPos;
                    }, 0);
                }
                field.onChange(newValue);
                return;
            }
            field.onChange(baseValue);
        };

    return (
        <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field, fieldState }) => (
                <Textarea
                    {...field}
                    {...register(name, {
                        required,
                    })}
                    ref={(e) => {
                        field.ref(e);
                        textareaRef.current = e;
                    }}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                    className="min-h-32"
                    placeholder={placeholder}
                    onChange={handleChange(field)}
                    value={field.value}
                    id={id}
                    invalid={!!fieldState.error?.message}
                />
            )}
        />
    );
};

export const TextareaWithSignature = React.memo(TextareaWithSignatureInner) as <
    T,
>(
    props: TextareaWithSignatureProps<T>,
) => JSX.Element;
